/**
 * 智绘画板
 */
import { request } from "../request";
import type { 
  BoardListReq,
  BoardGenerateReq,
  BoardEditReq,
  BoardResp,
  LayerSaveReq,
  LayerResp,
  PageResp,
  CanvasListRequest,
  CanvasListResponse
} from "@xmiles/holopix-types";

/**
 * 查询画板列表
 */
export const queryCanvasList = (data: CanvasListRequest) => {
  return request<CanvasListResponse>({
    url: "/pino_service_api/api/canvas/list",
    method: "post", 
    data: {
      data
    },
    isAuth: true,
    isEncryption: true,
  });
};

/**
 * 查询我的画板列表
 */
export const queryMyCanvasList = (data: CanvasListRequest) => {
  return request<CanvasListResponse>({
    url: "/pino_service_api/api/canvas/my-list",
    method: "post",
    data: {
      data
    },
    isAuth: true,
    isEncryption: true,
  });
};

/**
 * 根据名称搜索画板
 */
export const searchCanvasList = (data: CanvasListRequest) => {
  return request<CanvasListResponse>({
    url: "/pino_service_api/api/canvas/search",
    method: "post",
    data: {
      data
    },
    isAuth: true,
    isEncryption: true,
  });
};

/**
 * 获取画板列表
 */
export const getBoardList = (params: BoardListReq ) => {
  return request<PageResp<BoardResp>>({
    url: '/pino_service_api/api/board/list',
    method: 'post',
    data: {
      data: params
    },
    isAuth: true,
    isEncryption: true,
  });
};

/**
 * 获取画板详情
 */
export const getBoardDetail = (id: number) => {
  return request<BoardResp>({
    url: `/pino_service_api/api/board/detail/${id}`,
    method: 'get',
    isAuth: true,
    isEncryption: true,
  });
};

/**
 * 创建画板
 */
export const createBoard = (data: BoardGenerateReq) => {
  return request<BoardResp>({
    url: '/pino_service_api/api/board/generate',
    method: 'post',
    data: {
      data
    },
    isAuth: true,
    isEncryption: true,
  });
};

/**
 * 更新画板属性
 */
export const updateBoard = (data: BoardEditReq) => {
  return request<boolean>({
    url: '/pino_service_api/api/board/edit',
    method: 'PUT',
    data: {
      data
    },
    isAuth: true,
    isEncryption: true,
  });
};

/**
 * 删除画板
 */
export const deleteBoard = (id: number) => {
  return request<boolean>({
    url: `/pino_service_api/api/board/delete/${id}`,
    method: 'DELETE',
    isAuth: true,
    isEncryption: true,
  });
};

/**
 * 复制画板(另存为)
 */
export const duplicateBoard = (originalBoardId: number) => {
  return request<BoardResp>({
    url: `/pino_service_api/api/board/copy/${originalBoardId}`,
    method: 'post',
    isAuth: true,
    isEncryption: true,
  });
};

/**
 * 获取图层数据
 */
export const getLayerList = (boardId: number) => {
  return request<LayerResp>({
    url: '/pino_service_api/api/layer/list',
    method: 'post',
    data: {
      data: { boardId }
    },
    isAuth: true,
    isEncryption: true,
  });
};

/**
 * 保存图层数据
 */
export const saveLayerList = (data: LayerSaveReq) => {
  return request<boolean>({
    url: '/pino_service_api/api/layer/save',
    method: 'post',
    data: {
      data
    },
    isAuth: true,
    isEncryption: true,
  });
};

/**
 * 复制画板并重命名 - 兼容旧版本接口
 */
export const duplicateBoardWithName = async ({
  originalBoardId,
  newBoardName
}: {
  originalBoardId: number;
  newBoardName: string;
}) => {
  // 先复制画板
  const duplicateResult = await duplicateBoard(originalBoardId);
  
  // 如果复制成功且有新名称，则更新画板名称
  if (duplicateResult?.id && newBoardName) {
    await updateBoard({
      id: duplicateResult.id,
      boardName: newBoardName
    });
  }
  
  return {
    originalBoardId,
    newBoardName,
    newBoardId: duplicateResult?.id
  };
};

/**
 * 导出PSD算力扣减
 */
export const exportPSDPointsSub = (data: {
  batchSize?: number;
  teamId?: number;
  userId?: number;
  clientId?: string;
}) => {
  return request({
    url: '/pino_service_api/api/calcPoints/sub',
    method: 'post',
    data: {
      data: {
        ...data,
        batchSize: data.batchSize || 1,
        module: 27
      }
    },
    isAuth: true,
    isEncryption: true,
  });
};